import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import LazyImage from '@/components/LazyImage';
import SEO from '@/components/SEO';
import { seoData } from '@/utils/seoData';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Wrench,
  Disc,
  Droplet,
  Zap,
  Wind,
  Gauge,
  Battery,
  Cog,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';

export const ServicesPage = () => {
  const services = [
    {
      icon: <Cog className="w-10 h-10" />,
      title: 'Engine Repair & Diagnostics',
      description: 'Complete engine diagnostics, repair, and rebuilding services. Our experts handle everything from minor tune-ups to major overhauls.',
      features: ['Engine diagnostics', 'Timing belt replacement', 'Cylinder head repair', 'Engine rebuilding'],
      image: '/images/workshop.jpg',
    },
    {
      icon: <Disc className="w-10 h-10" />,
      title: 'Brake Service & Repair',
      description: 'Professional brake inspection, maintenance, and repair to ensure your safety on the road.',
      features: ['Brake pad replacement', 'Rotor resurfacing', 'Brake fluid flush', 'ABS system repair'],
      image: '/images/tire-service.jpg',
    },
    {
      icon: <Droplet className="w-10 h-10" />,
      title: 'Oil Change & Lubrication',
      description: 'Regular oil changes and lubrication services to keep your engine running smoothly.',
      features: ['Engine oil change', 'Filter replacement', 'Lubrication service', 'Fluid top-ups'],
      image: '/images/car-service.jpg',
    },
    {
      icon: <Zap className="w-10 h-10" />,
      title: 'Electrical System',
      description: 'Expert diagnosis and repair of all electrical components and systems.',
      features: ['Battery testing', 'Alternator repair', 'Starter replacement', 'Wiring repairs'],
      image: '/images/hero-mechanic.jpg',
    },
    {
      icon: <Wind className="w-10 h-10" />,
      title: 'AC & Heating',
      description: 'Complete air conditioning and heating system service and repair.',
      features: ['AC recharge', 'Compressor repair', 'Heater core service', 'Climate control repair'],
      image: '/images/workshop.jpg',
    },
    {
      icon: <Gauge className="w-10 h-10" />,
      title: 'Suspension & Steering',
      description: 'Suspension and steering system inspection, maintenance, and repair services.',
      features: ['Shock absorber replacement', 'Wheel alignment', 'Power steering repair', 'Ball joint replacement'],
      image: '/images/tire-service.jpg',
    },
    {
      icon: <Battery className="w-10 h-10" />,
      title: 'Battery Service',
      description: 'Battery testing, replacement, and electrical system diagnostics.',
      features: ['Battery testing', 'Battery replacement', 'Terminal cleaning', 'Charging system check'],
      image: '/images/car-service.jpg',
    },
    {
      icon: <Wrench className="w-10 h-10" />,
      title: 'General Maintenance',
      description: 'Comprehensive vehicle maintenance to prevent problems before they start.',
      features: ['Multi-point inspection', 'Preventive maintenance', 'Fluid checks', 'Safety inspection'],
      image: '/images/hero-mechanic.jpg',
    },
  ];

  const benefits = [
    'Certified and experienced mechanics',
    'Genuine parts and quality materials',
    'Competitive pricing and transparent quotes',
    'Quick turnaround times',
    'Comprehensive warranty on all services',
    'State-of-the-art diagnostic equipment',
  ];

  return (
    <div className="min-h-screen bg-background noise-bg">
      <SEO 
        title={seoData.services.title}
        description={seoData.services.description}
        keywords={seoData.services.keywords}
        ogImage={seoData.services.ogImage}
        structuredData={seoData.services.structuredData}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Our <span className="gradient-text">Services</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Comprehensive automotive repair and maintenance services for all vehicle makes and models.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-3d bg-card border-border hover:border-primary/50 transition-all duration-300">
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <LazyImage
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 service-image"
                    fadeInDuration={0.7}
                    pulseLoader={true}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-16 h-16 rounded-xl bg-primary/90 flex items-center justify-center text-primary-foreground">
                    {service.icon}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-muted-foreground mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 text-center">
              Why Choose Our Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                  </div>
                  <p className="text-lg text-muted-foreground">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Need Service for Your Vehicle?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us today to schedule an appointment or get a free quote for your vehicle repair needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Schedule Service
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/brands">
                <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  View Supported Brands
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;